import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationCancel, NavigationEnd, NavigationError } from '@angular/router';
import { environment } from '@intranet/env';
import { interval, switchMap } from 'rxjs';
import { LoginService } from './modules';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { HotkeyService } from './core/services/hotkey.service';
import { TimerSyncService } from './core/services/timer-sync.service';
import { DesktopAppService } from './core/services/desktop-app.service';
import { UpdateService } from './core/services/update.service';
import { TrackReminderBridgeService } from './core/services/track-reminder-bridge.service';
import { BuildVersionService } from './core/services/build-version.service';
import { ConnectivityService } from './core/services/connectivity.service';
import { CrashRecoveryService } from './core/services/crash-recovery.service';
import { TimerGapCoordinatorService } from './core/services/timer-gap-coordinator.service';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
    private loginPollHandle: any = null;

    constructor(
        private _loginService: LoginService,
        public router: Router,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private hotkeyService: HotkeyService, // Inject HotkeyService
        private timerSyncService: TimerSyncService,
        private desktopApp: DesktopAppService,
        // Eager instantiation — wires up the IPC subscription for auto-update
        // events from the moment the app boots, even before /desktop is loaded.
        // The UpdateBannerComponent (in the lazy desktop module) reuses the
        // same singleton so by the time it renders, state is already populated.
        // Underscore-prefix marks it as injected purely for the side effect.
        private _updateService: UpdateService,
        // Eager instantiation — bridges Angular timer state to the main-
        // process "Don't forget to track" reminder via IPC. No-op outside
        // the Visarc Timer Electron shell.
        private _trackReminderBridge: TrackReminderBridgeService,
        // Eager instantiation — starts polling /assets/version.json so we
        // can detect when a new bundle has been deployed (desktop banner,
        // and full page load on the next page change in the browser).
        private _buildVersionService: BuildVersionService,
        // Eager instantiation — starts the connectivity heartbeat at app
        // boot so the offline overlay is ready the moment the user lands
        // on /desktop. No-op outside the Visarc Timer Electron shell.
        private _connectivityService: ConnectivityService,
        // Eager instantiation — runs the localStorage heartbeat startup
        // check immediately so the recovery prompt can fire as soon as
        // the desktop shell loads (rather than waiting until something
        // else first injects the service).
        private _crashRecoveryService: CrashRecoveryService,
        // Eager instantiation — must subscribe to the idle and
        // crash-recovery sources before either has a chance to emit, so
        // no event is missed. Serialises both prompts into one queue.
        private _timerGapCoordinator: TimerGapCoordinatorService,
    ) {
        this.matIconRegistry.addSvgIcon(
            'rocks',
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/images/ico-rock.svg')
        );
    }

    /** True until the app's first page has arrived (see app.component.html). */
    showStartupLoading = true;

    ngOnInit() {
        this.timerSyncService.init();

        // Hide the startup loading screen once the first page has arrived —
        // i.e. the first time no navigation is left in progress (a cancelled
        // one, e.g. redirected to /login, is followed by the new one).
        const startupNav = this.router.events
            .pipe(filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError))
            .subscribe(() => {
                setTimeout(() => {
                    if (!this.showStartupLoading || this.router.getCurrentNavigation()) return;
                    this.showStartupLoading = false;
                    startupNav.unsubscribe();
                });
            });
        // Belt-and-braces: should the first page already have arrived before
        // the above was listening, don't leave the loading screen up.
        setTimeout(() => {
            if (this.showStartupLoading && this.router.navigated && !this.router.getCurrentNavigation()) {
                this.showStartupLoading = false;
                startupNav.unsubscribe();
            }
        });

        // Existing login logic
        if (environment.production) {
            interval(10000)
                .pipe(switchMap(() => this._loginService.ping()))
                .subscribe();
        }

        this._loginService.checkLogin().subscribe((u) => {
            if (this._loginService.redirectUrl) {
                let url = this._loginService.redirectUrl;
                this._loginService.redirectUrl = null;
                this.router.navigateByUrl(url);
                return;
            }
            // Inside the Visarc Timer Electron shell, always land on /desktop
            // after a successful login. The MSAL redirect drops the in-memory
            // redirectUrl, so this is the fallback when the user logs in fresh.
            if (u && this.desktopApp.isTimerApp) {
                this.router.navigateByUrl('/desktop');
            }
        });

        // If the user ever lands back on /login while already authenticated inside
        // the Electron shell (e.g. by clicking a stray link), bounce them to /desktop.
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event) => {
                const nav = event as NavigationEnd;
                if (this.desktopApp.isTimerApp
                    && this._loginService.isLoggedIn
                    && nav.urlAfterRedirects.startsWith('/login')) {
                    this.router.navigateByUrl('/desktop');
                }
            });

        // Belt-and-braces: poll login state inside the timer app. The MSAL
        // redirect login completes asynchronously and may flip `isLoggedIn` to
        // true *after* both the checkLogin subscription and the NavigationEnd
        // listener have already fired. Polling guarantees we catch the
        // transition. No-op outside the timer app.
        //
        // Stops as soon as `isLoggedIn` is true (whether or not we're still on
        // /login — once login is done there's nothing more to watch for) or
        // after a 60s cap, so an abandoned / failed login flow doesn't leave
        // a 300ms tick running for the lifetime of the app.
        if (this.desktopApp.isTimerApp) {
            const POLL_MS = 300;
            const POLL_MAX_MS = 60_000;
            const startedAt = Date.now();
            this.loginPollHandle = setInterval(() => {
                const isLoggedIn = this._loginService.isLoggedIn;
                const timedOut = Date.now() - startedAt >= POLL_MAX_MS;
                if (!isLoggedIn && !timedOut) return;
                clearInterval(this.loginPollHandle);
                this.loginPollHandle = null;
                if (isLoggedIn && this.router.url.startsWith('/login')) {
                    this.router.navigateByUrl('/desktop');
                }
            }, POLL_MS);
        }

        this.router.events
          .pipe(filter(event => event instanceof NavigationEnd))
          .subscribe(() => {
              const pageEl = document.getElementById('page');

              if (pageEl) {
                  // Main scroll container
                  pageEl.scrollTo({ top: 0, left: 0, behavior: 'auto' });
              } else {
                  // Fallback to window if #page isn't present
                  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
              }
          });
    }

    ngOnDestroy(): void {
        // AppComponent is the root and rarely teardown-tested in production,
        // but during HMR / hot reload the component is recreated — clear the
        // interval so we don't double up.
        if (this.loginPollHandle) {
            clearInterval(this.loginPollHandle);
            this.loginPollHandle = null;
        }
    }

    // Global keyboard listener
    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent): void {
        this.hotkeyService.handleKeyPress(event); // Delegate key press to HotkeyService
    }
}
